import { Component, inject, signal, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { ExplanationJsonModel } from '../../explanation.json.model';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { AppSelectors } from '../../../app.state';
import { Store } from '@ngxs/store';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { LearnItemMetaDataComponentMode } from '../../../lesson/learnitem-metadata/learnitem-metadata.component';
import { MIN_NAME_LENGTH } from '../../../core/util';

@Component({
  selector: 'pixl-edit-explanation',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    MatTableModule,
    ReactiveFormsModule,
    CommonModule,
  ],
  providers: [],
  templateUrl: './edit-explanation.component.html',
  styleUrl: './edit-explanation.component.scss'
})
export class EditExplanationComponent implements OnInit {
  protected readonly LearnItemMetaDataComponentMode = LearnItemMetaDataComponentMode;
  private readonly dialogRef = inject(MatDialogRef<EditExplanationComponent>);
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private activeConsumer: ConsumerJsonModel | undefined;
  private appState$ = this.store.select(AppSelectors.appState);

  public formGroup!: FormGroup;
  public editExplanation: ExplanationJsonModel = new ExplanationJsonModel();
  public approval = signal(false);
  public consumerTabName = signal('');
  public functionalHelpName = signal('');
  public explanationName = signal('');
  public showGlobalMetaDataView = signal(false);
  public data = inject<ExplanationJsonModel>(MAT_DIALOG_DATA);
  public consumers: ConsumerJsonModel[] = [];
  public competencies: SubjectJsonModel[] = [];

  constructor() {
    this.approval.set(this.data.approval === 'Dormant');

    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.competencies = subjects;

        if (this.data.consumerId) {
          this.activeConsumer = this.consumers.find((consumer: ConsumerJsonModel) => {
            return consumer.id === this.data.consumerId;
          });

          if (this.activeConsumer) {
            this.consumerTabName.set(this.activeConsumer.name);
          }
        }

        if (this.data.name) {
          this.explanationName.set(this.data.name);
        }
      }
    });

    this.formGroup = this.fb.group({
      consumerId: new FormControl({ value: this.data.consumerId || undefined, disabled: this.approval() }, Validators.required),
      subjectId: new FormControl({ value: this.data.subjectId || undefined, disabled: this.approval() }, Validators.required),
      name: new FormControl({ value: this.data.name || '', disabled: this.approval() }, [Validators.required, Validators.minLength(MIN_NAME_LENGTH)]),
      copyRestricted: new FormControl({ value: this.data.copyRestricted || false, disabled: this.approval() }),
    });
  }

  public ngOnInit() {
    this.formGroup.markAllAsTouched();
  }

  public checkForNameErrors(formControl: AbstractControl): string | null {
    if (!formControl) {
      return null;
    }

    if (0 === formControl.value.length) {
      return 'Eingabe erforderlich';
    }

    if (MIN_NAME_LENGTH > formControl.value.length) {
      return `Eingabe muss mindestens ${MIN_NAME_LENGTH} Zeichen haben`;
    }

    return null;
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      this.data.update(this.formGroup.value);
      this.dialogRef.close(this.data);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
